import { FC, FormEvent, useEffect, useState } from 'react';

import { createLead } from '../../api/leads';
import { LeadCreatePayload } from '../../data/types';
import {
  LEAD_CATEGORY_OPTIONS,
  LEAD_RESOURCE_TYPE_OPTIONS,
  LEAD_STAGE_OPTIONS,
  LEAD_STATUS_OPTIONS,
  US_STATE_OPTIONS,
} from '../../utils/leadsAdmin';

type LeadCreateModalProps = {
  adminEmail: string;
  onClose: () => void;
  onComplete: (message: string) => void;
};

const defaultDraft: LeadCreatePayload = {
  lead_category: 'food_entrepreneur',
  lead_stage: 'lead_generation',
  lead_status: 'new_lead',
  resource_types: [],
  is_test_lead: false,
  send_welcome_email: false,
};

/** Modal for manually adding a lead from the CRM dashboard. */
export const LeadCreateModal: FC<LeadCreateModalProps> = ({ adminEmail, onClose, onComplete }) => {
  const [draft, setDraft] = useState<LeadCreatePayload>(defaultDraft);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!draft.email?.trim() && !draft.business_name?.trim()) {
      setError('Email or business name is required.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const result = await createLead(draft, adminEmail);
      onComplete(`Lead ${result?.action ?? 'saved'} successfully.`);
      onClose();
    } catch {
      setError('Could not create lead.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-xl bg-white p-6">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold font-raleway">Add lead</h2>
            <p className="text-sm text-[#737373] font-open-sans">Create a lead manually without sending email by default.</p>
          </div>
          <button type="button" onClick={onClose} className="text-sm font-semibold font-raleway">
            Close
          </button>
        </div>

        <form onSubmit={handleSubmit} className="grid gap-3 md:grid-cols-2">
          {([
            ['email', 'Email'],
            ['first_name', 'First name'],
            ['last_name', 'Last name'],
            ['business_name', 'Business name'],
            ['business_website', 'Website'],
            ['phone', 'Phone'],
            ['city', 'City'],
            ['county', 'County'],
            ['business_zip_code', 'Zipcode'],
            ['account_manager', 'Account manager'],
          ] as const).map(([field, label]) => (
            <label key={field} className="text-sm font-raleway">
              {label}
              <input
                className="mt-1 w-full rounded-lg border border-[#E9E9E9] px-3 py-2 font-open-sans text-sm"
                value={(draft[field] as string) ?? ''}
                onChange={(event) => setDraft((current) => ({ ...current, [field]: event.target.value }))}
              />
            </label>
          ))}

          <label className="text-sm font-raleway">
            State
            <select
              className="mt-1 w-full rounded-lg border border-[#E9E9E9] px-3 py-2 font-open-sans text-sm"
              value={draft.state ?? ''}
              onChange={(event) => setDraft((current) => ({ ...current, state: event.target.value }))}
            >
              <option value="">Select state / territory</option>
              {US_STATE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label} ({option.value})
                </option>
              ))}
            </select>
          </label>

          {([
            ['lead_category', 'Category', LEAD_CATEGORY_OPTIONS],
            ['lead_stage', 'Stage', LEAD_STAGE_OPTIONS],
            ['lead_status', 'Status', LEAD_STATUS_OPTIONS],
          ] as const).map(([field, label, options]) => (
            <label key={field} className="text-sm font-raleway">
              {label}
              <select
                className="mt-1 w-full rounded-lg border border-[#E9E9E9] px-3 py-2 font-open-sans text-sm"
                value={(draft[field] as string) ?? ''}
                onChange={(event) => setDraft((current) => ({ ...current, [field]: event.target.value }))}
              >
                {options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          ))}

          <label className="text-sm md:col-span-2 font-raleway">
            Resource types
            <select
              multiple
              className="mt-1 min-h-[88px] w-full rounded-lg border border-[#E9E9E9] px-3 py-2 font-open-sans text-sm"
              value={draft.resource_types ?? []}
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  resource_types: Array.from(event.target.selectedOptions).map((option) => option.value),
                }))
              }
            >
              {LEAD_RESOURCE_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="text-sm md:col-span-2 font-raleway">
            Notes
            <textarea
              className="mt-1 min-h-[80px] w-full rounded-lg border border-[#E9E9E9] px-3 py-2 font-open-sans text-sm"
              value={draft.notes ?? ''}
              onChange={(event) => setDraft((current) => ({ ...current, notes: event.target.value }))}
            />
          </label>

          <label className="flex items-center gap-2 text-sm font-open-sans md:col-span-2">
            <input
              type="checkbox"
              checked={Boolean(draft.is_test_lead)}
              onChange={(event) => setDraft((current) => ({ ...current, is_test_lead: event.target.checked }))}
            />
            Mark as test lead (no real emails or side effects)
          </label>

          <label className="flex items-center gap-2 text-sm font-open-sans md:col-span-2">
            <input
              type="checkbox"
              checked={Boolean(draft.send_welcome_email)}
              onChange={(event) => setDraft((current) => ({ ...current, send_welcome_email: event.target.checked }))}
            />
            Send welcome email
          </label>

          {error ? <p className="md:col-span-2 text-sm text-red-600 font-open-sans">{error}</p> : null}

          <div className="md:col-span-2">
            <button
              type="submit"
              disabled={loading}
              className="rounded-lg bg-[#013847] px-4 py-2 text-sm font-semibold text-white font-raleway disabled:opacity-50"
            >
              {loading ? 'Saving…' : 'Create lead'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LeadCreateModal;
